import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Upload, ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import FileUpload from '../../components/FileUpload';
import FormInput from '../../components/FormInput';
import SelectInput from '../../components/SelectInput';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';

function ReportUpload() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [status, setStatus] = useState('READY');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  async function onSubmit(e) {
    e.preventDefault();
    if (!file) { toast.error('Pick a file first'); return; }
    setSubmitting(true);
    setProgress(0);
    try {
      const report = await vendorApi.uploadReport(orderId, file, { remarks, status }, setProgress);
      toast.success('Report uploaded');
      navigate(`/reports/${report._id}`);
    } catch (e1) {
      toast.error(errorMessage(e1));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Orders', to: '/orders' },
          { label: 'Order', to: `/orders/${orderId}` },
          { label: 'Upload report' }
        ]}
        title="Upload medical report"
        subtitle="Upload the patient's report. Allowed: PDF, JPG, PNG · max 10 MB."
        actions={
          <Link to={`/orders/${orderId}`} className="btn"><ArrowLeft size={14} /> Back to order</Link>
        }
      />

      <form onSubmit={onSubmit} className="card" style={{ maxWidth: 720 }}>
        <FileUpload value={file} onChange={setFile} maxBytes={10 * 1024 * 1024} />

        <div style={{ marginTop: 'var(--s-4)' }}>
          <SelectInput
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={[
              { value: 'READY', label: 'Ready (visible to patient)' },
              { value: 'AMENDED', label: 'Amended' }
            ]}
          />
          <FormInput
            label="Remarks (optional)"
            textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Notes for the patient or admin…"
          />
        </div>

        {submitting && (
          <div style={{ marginTop: 'var(--s-3)' }}>
            <div className="bar-track"><div className="bar-fill" style={{ width: `${progress}%` }} /></div>
            <div className="muted text-xs" style={{ marginTop: 6 }}>Uploading… {progress}%</div>
          </div>
        )}

        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <button type="submit" className="btn btn-primary" disabled={submitting || !file}>
            {submitting ? <span className="spinner" /> : <Upload size={16} />}
            Upload report
          </button>
          <Link to={`/orders/${orderId}`} className="btn btn-ghost">Cancel</Link>
        </div>
      </form>
    </>
  );
}

export default ReportUpload;
